import { FFH_HOSTNAME, SITE_NAME } from '@lib/constants';
import {
  buildOrganisationJsonLd,
  type BreadcrumbEntry
} from '@lib/page-utils/structured-data';
import type {
  SanityArticlePage,
  SanityAuthor,
  SanityPredictedLineupsPage
} from '@lib/sanity/types';

const CATEGORY_PATH_PREFIX = '/category/';

// ArticleHeader renders this trail as well, so the BreadcrumbList and the
// on-page crumbs always agree. There is no /fpl hub page; the FPL category
// listing is the closest thing to one.
export const ARTICLE_SITE_SECTION: BreadcrumbEntry = {
  label: 'Fantasy Premier League',
  path: `${CATEGORY_PATH_PREFIX}fpl`
};
export const TEAM_REVEALS_SECTION_LABEL = 'Team reveals';
// Reveals carry no category, so they crumb to the listing the homepage links.
const TEAM_REVEALS_SECTION: BreadcrumbEntry = {
  label: TEAM_REVEALS_SECTION_LABEL,
  path: `${CATEGORY_PATH_PREFIX}team-reveal`
};

export const buildCategoryPath = (categorySlug: string): string =>
  `${CATEGORY_PATH_PREFIX}${categorySlug}`;

/**
 * The gated half of a premium article. Google's paywalled-content markup names
 * it by CSS selector, so PremiumBody must keep this class on whatever it
 * renders in that slot.
 */
export const PREMIUM_BODY_CLASS_NAME = 'article-premium-body';

export const SITE_TWITTER_HANDLE = '@FFH_HQ';
export const FACEBOOK_PAGE_URL = 'https://www.facebook.com/ffootballhub/';
const TWITTER_PROFILE_BASE_URL = 'https://twitter.com/';
const AUTHOR_PATH_PREFIX = '/author/';
// Some author slugs arrive wrapped in slashes (a Sanity author saved as
// "/chris_tan/"), which would join into "/author//chris_tan/".
const EDGE_SLASHES_PATTERN = /^\/+|\/+$/g;

export const buildAuthorPath = (authorSlug: string): string =>
  `${AUTHOR_PATH_PREFIX}${authorSlug.replace(EDGE_SLASHES_PATTERN, '')}`;

export type ArticleShareImage = {
  url: string;
  width?: number;
  height?: number;
};

export type ArticleJsonLdInput = {
  page: SanityArticlePage;
  canonicalUrl: string;
  description?: string;
  image?: ArticleShareImage;
  wordCount: number;
};

/**
 * The 52-character site strapline truncated every article title in search
 * results, so editorial pages carry the short brand name instead.
 */
export const buildArticleMetaTitle = (page: SanityArticlePage): string =>
  page.seo?.metaTitle || `${page.title} | ${SITE_NAME}`;

/**
 * The date fields both resolvers read. Sitemap queries project them straight
 * from GROQ, which returns null for an unset field.
 */
export type ArticleDates = {
  publishedAt?: string | null;
  lastUpdatedAt?: string | null;
  _createdAt?: string | null;
};

/**
 * Editors set `publishedAt` by hand and can leave it empty, so the document's
 * creation stamp stands in rather than dropping the date from the graph.
 */
export const resolvePublishedAt = (page: ArticleDates): string | undefined =>
  page.publishedAt ?? page._createdAt ?? undefined;

/**
 * The editor's last deliberate update, matching the Updated tag. Sanity's own
 * `_updatedAt` moves on any save, so it would report trivial edits as updates.
 */
export const resolveModifiedAt = (page: ArticleDates): string | undefined =>
  page.lastUpdatedAt ?? resolvePublishedAt(page);

export const buildArticleSection = (
  page: SanityArticlePage
): string | undefined =>
  page._type === 'teamReveal'
    ? TEAM_REVEALS_SECTION_LABEL
    : page.category?.title ?? undefined;

export const buildArticleBreadcrumbs = (
  page: SanityArticlePage
): BreadcrumbEntry[] => {
  if (page._type === 'teamReveal') {
    return [ARTICLE_SITE_SECTION, TEAM_REVEALS_SECTION];
  }
  if (!page.category) {
    return [ARTICLE_SITE_SECTION];
  }
  return [
    ARTICLE_SITE_SECTION,
    {
      label: page.category.title,
      ...(page.category.slug
        ? { path: buildCategoryPath(page.category.slug) }
        : {})
    }
  ];
};

export const toTwitterHandle = (handle: string): string =>
  handle.trim().startsWith('@') ? handle.trim() : `@${handle.trim()}`;

const toTwitterProfileUrl = (handle: string): string =>
  `${TWITTER_PROFILE_BASE_URL}${toTwitterHandle(handle).slice(1)}`;

/**
 * The share image is resized to a fixed width, so the height only follows
 * when the source dimensions are known. Sanity does not upscale, so a narrower
 * source keeps its own width.
 */
export const scaleShareImageDimensions = (
  targetWidth: number,
  dimensions: { width: number; height: number } | undefined
): { width: number; height: number } | undefined => {
  if (!dimensions || dimensions.width <= 0 || dimensions.height <= 0) {
    return undefined;
  }
  const width = Math.min(targetWidth, dimensions.width);
  return {
    width,
    height: Math.round((width * dimensions.height) / dimensions.width)
  };
};

const buildAuthorJsonLd = (
  author: Pick<SanityAuthor, 'name' | 'slug' | 'twitterHandle'>
): Record<string, unknown> => ({
  '@type': 'Person',
  name: author.name,
  url: `${FFH_HOSTNAME}${buildAuthorPath(author.slug)}`,
  ...(author.twitterHandle
    ? { sameAs: [toTwitterProfileUrl(author.twitterHandle)] }
    : {})
});

const buildImageObjectJsonLd = (
  image: ArticleShareImage
): Record<string, unknown> => ({
  '@type': 'ImageObject',
  url: image.url,
  ...(image.width ? { width: image.width } : {}),
  ...(image.height ? { height: image.height } : {})
});

/**
 * One Article graph for articles and team reveals. Premium documents declare
 * the gated section so a teaser followed by a paywall reads as legitimate
 * gated content. Only the free body is ever in the static HTML, so wordCount
 * describes what a crawler can read, not the whole piece.
 */
export const buildArticleJsonLd = ({
  page,
  canonicalUrl,
  description,
  image,
  wordCount
}: ArticleJsonLdInput): Record<string, unknown> => {
  const publisher = buildOrganisationJsonLd();
  const section = buildArticleSection(page);
  const isPremium = page.accessLevel === 'premium';
  const publishedAt = resolvePublishedAt(page);
  const modifiedAt = resolveModifiedAt(page);

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${canonicalUrl}#article`,
    headline: page.title,
    ...(description ? { description } : {}),
    ...(publishedAt ? { datePublished: publishedAt } : {}),
    ...(modifiedAt ? { dateModified: modifiedAt } : {}),
    author: page.author ? buildAuthorJsonLd(page.author) : publisher,
    publisher,
    ...(image ? { image: buildImageObjectJsonLd(image) } : {}),
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
    ...(section ? { articleSection: section } : {}),
    wordCount,
    ...(isPremium
      ? {
          isAccessibleForFree: false,
          hasPart: {
            '@type': 'WebPageElement',
            isAccessibleForFree: false,
            cssSelector: `.${PREMIUM_BODY_CLASS_NAME}`
          }
        }
      : {})
  };
};

export type PredictedLineupsJsonLdInput = {
  page: Pick<
    SanityPredictedLineupsPage,
    'title' | '_createdAt' | '_updatedAt' | 'author'
  >;
  canonicalUrl: string;
  description?: string;
  image?: ArticleShareImage;
};

/**
 * The predicted lineups page as an Article, crediting its author the way
 * articles do. One evergreen URL is rewritten every gameweek, so it was first
 * published when the document was created and modified at each publish. The
 * gated club sections never reach the static HTML, so there is no paywall
 * markup to declare.
 */
export const buildPredictedLineupsJsonLd = ({
  page,
  canonicalUrl,
  description,
  image
}: PredictedLineupsJsonLdInput): Record<string, unknown> => {
  const publisher = buildOrganisationJsonLd();

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${canonicalUrl}#article`,
    headline: page.title,
    ...(description ? { description } : {}),
    ...(page._createdAt ? { datePublished: page._createdAt } : {}),
    ...(page._updatedAt ? { dateModified: page._updatedAt } : {}),
    author: page.author ? buildAuthorJsonLd(page.author) : publisher,
    publisher,
    ...(image ? { image: buildImageObjectJsonLd(image) } : {}),
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl }
  };
};
